import { useEffect, useMemo, useRef } from 'react';
import { DayOfWeek } from '../../generated/gql-types';
import {
  formatJobDaysSummary,
  getSelectableWorkingDays,
  toggleJobWorkingDay,
} from './workingDays.util';
import { useTemplateStore } from '../../template/useTemplateStore';
import { pluralize } from '../../util/utils';

export type UseJobWorkingDaysParams = {
  startDate: string | Date | null | undefined;
  endDate: string | Date | null | undefined;
  selectedDays: DayOfWeek[];
  onSelectedDaysChange: (days: DayOfWeek[]) => void;
  numberOfWorkers: number;
};

export type UseJobWorkingDaysResult = {
  /**
   * Days shown in the picker, in week order.
   * Empty when the schedule spans fewer than 3 inclusive calendar days.
   * Full Mon–Sun when the span is 7 days or longer; otherwise only the days
   * the range covers.
   */
  selectableDays: DayOfWeek[];
  /** True when the working-days picker should show (span ≥ 3 days). */
  isVisible: boolean;
  toggleDay: (day: DayOfWeek) => void;
  /** "Mon – Fri" for the current selection. */
  workingDaysSummary: string;
  /** " Candidates will be available Sat, Sun." when any selectable day is off. */
  daysOffSummary: string;
  /**
   * Call before restoring a saved selection for a schedule so the auto-sync
   * does not overwrite it (e.g. copying a draft job back into the form).
   */
  preserveSelectionForSchedule: (
    startDate: string | Date | null | undefined,
    endDate: string | Date | null | undefined,
  ) => void;
};

/**
 * Working-days selection for job create/edit forms.
 *
 * Hidden for schedules shorter than 3 inclusive calendar days. For 3–6 days the
 * picker shows only the weekdays the range covers (Monday-first). For 7+ days it
 * shows the full Mon–Sun week. Every day of a new range starts selected.
 */
export const useJobWorkingDays = ({
  startDate,
  endDate,
  selectedDays,
  onSelectedDaysChange,
  numberOfWorkers = 1,
}: UseJobWorkingDaysParams): UseJobWorkingDaysResult => {
  const selectableDays = useMemo(
    () => getSelectableWorkingDays(startDate, endDate),
    [startDate, endDate],
  );

  const syncedWorkingDaysRef = useRef('');
  const onSelectedDaysChangeRef = useRef(onSelectedDaysChange);
  onSelectedDaysChangeRef.current = onSelectedDaysChange;
  const template = useTemplateStore((state) => state.template);

  useEffect(() => {
    const selectableDaysKey = selectableDays.join();
    if (syncedWorkingDaysRef.current === selectableDaysKey) return;

    syncedWorkingDaysRef.current = selectableDaysKey;
    onSelectedDaysChangeRef.current(selectableDays);
  }, [selectableDays]);

  const toggleDay = (day: DayOfWeek) => {
    onSelectedDaysChange(toggleJobWorkingDay(selectedDays, day));
  };

  const workingDaysSummary = useMemo(
    () => formatJobDaysSummary(selectedDays),
    [selectedDays],
  );

  const daysOffSummary = useMemo(() => {
    const daysOff = selectableDays.filter((day) => !selectedDays.includes(day));
    if (daysOff.length === 0) return '';
    return ` ${pluralize(template?._WORKER_TERM_ || '', numberOfWorkers)} will be available ${formatJobDaysSummary(daysOff)}.`;
  }, [selectableDays, selectedDays, numberOfWorkers]);

  const preserveSelectionForSchedule = (
    scheduleStartDate: string | Date | null | undefined,
    scheduleEndDate: string | Date | null | undefined,
  ) => {
    syncedWorkingDaysRef.current = getSelectableWorkingDays(
      scheduleStartDate,
      scheduleEndDate,
    ).join();
  };

  return {
    selectableDays,
    isVisible: selectableDays.length > 0,
    toggleDay,
    workingDaysSummary,
    daysOffSummary,
    preserveSelectionForSchedule,
  };
};
